import type { ConfettiParticle } from "react-confetti";
import { HALLOWEEN } from "@/theme/tokens";
import { BAT_PATH, BAT_VIEW_HEIGHT, BAT_VIEW_WIDTH } from "./halloweenShapes";

/** Wrapper colors for the wrapped candies and lollipops; candy corn and bats keep their own. */
export const HALLOWEEN_TREAT_COLORS = [HALLOWEEN.PUMPKIN, HALLOWEEN.CANDY_YELLOW, HALLOWEEN.BAT, HALLOWEEN.LEAF];

// react-confetti hands each piece a random shape 0–2; shape 2 is split between lollipops and bats by the piece's height.
const CANDY_CORN = 0;
const WRAPPED_CANDY = 1;
/** The piece's `h` is random 5–20, so about 60% of shape-2 pieces are lollipops and the rest bats. */
const LOLLIPOP_MAX_HEIGHT = 14;
/** A treat spans this many times the piece's radius (5–10px): 18–36px, so treats read as candy, not specks. */
const TREAT_SCALE = 3.6;
// Candy corn bands, from the wide end to the tip, as shares of its height.
const CORN_YELLOW_END = 0.36;
const CORN_ORANGE_END = 0.66;
const LOLLIPOP_SWIRL_TURNS = 2.5;
const LOLLIPOP_SWIRL_STEPS = 60;
const WRAPPER_STRIPES = [-0.5, 0, 0.5];
/** Points along a twist's crinkled end, as shares of its flare; they alternate in and out. */
const WRAPPER_CRINKLES = [-0.5, 0, 0.5];
const FULL_TURN = Math.PI * 2;
/** Bats stay roughly level (radians either way) and fold their wings to this share of their span as they flap. */
const BAT_TILT = 0.35;
const BAT_FOLDED = 0.45;
/** Bats are mostly wing, so they draw wider than the candies to look as big. */
const BAT_SCALE = 1.3;

const outline = (context: CanvasRenderingContext2D, path: Path2D, size: number) => {
	context.strokeStyle = HALLOWEEN.CANDY_CREASE;
	context.lineWidth = size * 0.035;
	context.lineJoin = "round";
	context.stroke(path);
};

const shine = (context: CanvasRenderingContext2D, x: number, y: number, radiusX: number, radiusY: number) => {
	context.fillStyle = HALLOWEEN.CANDY_GLOSS;
	context.beginPath();
	context.ellipse(x, y, radiusX, radiusY, -0.5, 0, FULL_TURN);
	context.fill();
};

/** Yellow at the domed wide end, orange in the middle, white at the rounded tip. */
const drawCandyCorn = (context: CanvasRenderingContext2D, size: number) => {
	const halfWidth = size * 0.32;
	const halfHeight = size * 0.5;
	const dome = size * 0.08;
	const kernel = new Path2D();
	kernel.moveTo(-halfWidth, -halfHeight + dome);
	kernel.quadraticCurveTo(0, -halfHeight - dome, halfWidth, -halfHeight + dome);
	kernel.bezierCurveTo(halfWidth, -halfHeight * 0.3, size * 0.07, halfHeight * 0.9, 0, halfHeight);
	kernel.bezierCurveTo(-size * 0.07, halfHeight * 0.9, -halfWidth, -halfHeight * 0.3, -halfWidth, -halfHeight + dome);
	context.save();
	context.clip(kernel);
	const bands = [
		{ color: HALLOWEEN.CANDY_YELLOW, from: 0, to: CORN_YELLOW_END },
		{ color: HALLOWEEN.PUMPKIN, from: CORN_YELLOW_END, to: CORN_ORANGE_END },
		{ color: HALLOWEEN.CANDY_WHITE, from: CORN_ORANGE_END, to: 1 },
	];
	for (const band of bands) {
		context.fillStyle = band.color;
		context.fillRect(-halfWidth, -halfHeight + size * band.from, halfWidth * 2, size * (band.to - band.from));
	}
	shine(context, -halfWidth * 0.45, -halfHeight * 0.25, size * 0.05, size * 0.2);
	context.restore();
	outline(context, kernel, size);
};

/** One crinkled twist of a wrapper, pinched against the candy and fanned out with pleats. */
const drawWrapperTwist = (context: CanvasRenderingContext2D, size: number, color: string, side: number) => {
	const pinch = size * 0.3;
	const end = size * 0.5;
	const flare = size * 0.22;
	const twist = new Path2D();
	twist.moveTo(side * size * 0.18, -size * 0.08);
	twist.lineTo(side * pinch, -size * 0.03);
	twist.lineTo(side * end, -flare);
	for (const [index, height] of WRAPPER_CRINKLES.entries()) {
		twist.lineTo(side * end * (index % 2 === 0 ? 0.9 : 1), flare * height);
	}
	twist.lineTo(side * end, flare);
	twist.lineTo(side * pinch, size * 0.03);
	twist.lineTo(side * size * 0.18, size * 0.08);
	twist.closePath();
	context.fillStyle = color;
	context.fill(twist);
	context.strokeStyle = HALLOWEEN.CANDY_CREASE;
	context.lineWidth = size * 0.025;
	for (const height of [-0.55, 0, 0.55]) {
		context.beginPath();
		context.moveTo(side * pinch, 0);
		context.lineTo(side * end * 0.95, flare * height);
		context.stroke();
	}
	outline(context, twist, size);
};

/** A round candy in a striped wrapper with a twist at each end. */
const drawWrappedCandy = (context: CanvasRenderingContext2D, size: number, color: string) => {
	const radius = size * 0.24;
	for (const side of [-1, 1]) drawWrapperTwist(context, size, color, side);
	const body = new Path2D();
	body.arc(0, 0, radius, 0, FULL_TURN);
	context.save();
	context.clip(body);
	context.fillStyle = color;
	context.fill(body);
	context.strokeStyle = HALLOWEEN.CANDY_WHITE;
	context.lineWidth = size * 0.06;
	for (const offset of WRAPPER_STRIPES) {
		context.beginPath();
		context.moveTo(radius * offset - radius, radius);
		context.lineTo(radius * offset + radius, -radius);
		context.stroke();
	}
	shine(context, -radius * 0.4, -radius * 0.4, radius * 0.32, radius * 0.18);
	context.restore();
	outline(context, body, size);
};

/** A round swirl lollipop on a white stick. */
const drawLollipop = (context: CanvasRenderingContext2D, size: number, color: string) => {
	const radius = size * 0.3;
	const centerY = -size * 0.14;
	const stick = new Path2D();
	stick.rect(-size * 0.035, 0, size * 0.07, size * 0.5);
	context.fillStyle = HALLOWEEN.CANDY_WHITE;
	context.fill(stick);
	outline(context, stick, size);
	const disc = new Path2D();
	disc.arc(0, centerY, radius, 0, FULL_TURN);
	context.fillStyle = color;
	context.fill(disc);
	context.strokeStyle = HALLOWEEN.CANDY_WHITE;
	context.lineWidth = size * 0.055;
	context.lineCap = "round";
	context.beginPath();
	for (let step = 0; step <= LOLLIPOP_SWIRL_STEPS; step += 1) {
		const progress = step / LOLLIPOP_SWIRL_STEPS;
		const turn = progress * LOLLIPOP_SWIRL_TURNS * FULL_TURN;
		context.lineTo(Math.cos(turn) * radius * 0.85 * progress, centerY + Math.sin(turn) * radius * 0.85 * progress);
	}
	context.stroke();
	shine(context, -radius * 0.4, centerY - radius * 0.4, radius * 0.3, radius * 0.16);
	outline(context, disc, size);
};

/** A bat with little yellow eyes. */
const drawBat = (context: CanvasRenderingContext2D, size: number) => {
	const scale = size / BAT_VIEW_WIDTH;
	context.scale(scale, scale);
	context.translate(-BAT_VIEW_WIDTH / 2, -BAT_VIEW_HEIGHT / 2);
	context.fillStyle = HALLOWEEN.HAT;
	context.fill(new Path2D(BAT_PATH));
	context.fillStyle = HALLOWEEN.CANDY_YELLOW;
	for (const eyeX of [15.9, 18.1]) {
		context.beginPath();
		context.arc(eyeX, 8.2, 0.75, 0, FULL_TURN);
		context.fill();
	}
};

/**
 * `react-confetti`'s `drawShape`: draws each piece as candy corn, a wrapped candy, a lollipop or a bat. It replaces
 * the library's paper-flip transform (which squashes and skews each piece) with a rigid spin, so treats tumble like
 * solid candy; bats stay level and flap instead.
 */
export function drawHalloweenTreat(this: ConfettiParticle, context: CanvasRenderingContext2D): void {
	const size = this.radius * TREAT_SCALE;
	context.setTransform(1, 0, 0, 1, this.x, this.y);
	if (this.shape === CANDY_CORN) {
		context.rotate(this.angle);
		drawCandyCorn(context, size);
	} else if (this.shape === WRAPPED_CANDY) {
		context.rotate(this.angle);
		drawWrappedCandy(context, size, this.color);
	} else if (this.h <= LOLLIPOP_MAX_HEIGHT) {
		context.rotate(this.angle);
		drawLollipop(context, size, this.color);
	} else {
		context.rotate(Math.sin(this.angle) * BAT_TILT);
		context.scale(BAT_FOLDED + (1 - BAT_FOLDED) * Math.abs(this.rotateY), 1);
		drawBat(context, size * BAT_SCALE);
	}
}
